export type CountdownSoundCueKind = "tick" | "final";

export interface CountdownSoundCue {
  readonly kind: CountdownSoundCueKind;
  readonly remainingSeconds: number;
  readonly frequenciesHz: readonly number[];
  readonly durationMs: number;
}

export interface CountdownAudioBackend {
  resume(): void | Promise<void>;
  play(cue: CountdownSoundCue): void;
  close(): void | Promise<void>;
}

export type CountdownAudioBackendFactory = () => CountdownAudioBackend | null;

/**
 * Emits at most one cue for each newly reached second from 10 through zero.
 * A backwards clock correction never replays a cue that the audience heard.
 */
export class CountdownCueSequencer {
  private lowestSeenSeconds = Number.POSITIVE_INFINITY;

  reset(): void {
    this.lowestSeenSeconds = Number.POSITIVE_INFINITY;
  }

  next(remainingSeconds: number): CountdownSoundCue | null {
    if (!Number.isSafeInteger(remainingSeconds) || remainingSeconds < 0) {
      throw new RangeError("remainingSeconds must be a non-negative safe integer");
    }
    if (remainingSeconds >= this.lowestSeenSeconds) return null;

    this.lowestSeenSeconds = remainingSeconds;
    if (remainingSeconds > 10) return null;

    if (remainingSeconds === 0) {
      return {
        kind: "final",
        remainingSeconds,
        frequenciesHz: [659.25, 987.77],
        durationMs: 650,
      };
    }

    return {
      kind: "tick",
      remainingSeconds,
      frequenciesHz: [720 + (10 - remainingSeconds) * 20],
      durationMs: remainingSeconds <= 5 ? 130 : 90,
    };
  }
}

function ignoreAudioFailure(value: void | Promise<void>): void {
  void Promise.resolve(value).catch(() => undefined);
}

/**
 * Audio contexts must be constructed or resumed inside a user gesture. The
 * settings submit handler calls armFromUserGesture; later countdown ticks only
 * schedule sound on that already-authorized context.
 */
export class CountdownSoundPlayer {
  private backend: CountdownAudioBackend | null = null;

  constructor(
    private readonly createBackend: CountdownAudioBackendFactory =
      createWebAudioCountdownBackend,
  ) {}

  armFromUserGesture(): boolean {
    if (this.backend === null) {
      try {
        this.backend = this.createBackend();
      } catch {
        this.backend = null;
      }
    }
    if (this.backend === null) return false;

    try {
      ignoreAudioFailure(this.backend.resume());
    } catch {
      // Unsupported or blocked audio remains a silent, non-fatal fallback.
    }
    return true;
  }

  play(cue: CountdownSoundCue): void {
    try {
      this.backend?.play(cue);
    } catch {
      // Projection must continue even when an audio device disappears.
    }
  }

  dispose(): void {
    const backend = this.backend;
    this.backend = null;
    if (backend === null) return;

    try {
      ignoreAudioFailure(backend.close());
    } catch {
      // Closing an already-disconnected device is harmless.
    }
  }
}

type AudioContextConstructor = new () => AudioContext;

function readAudioContextConstructor(): AudioContextConstructor | null {
  const audioGlobal = globalThis as typeof globalThis & {
    readonly webkitAudioContext?: AudioContextConstructor;
  };
  if (typeof audioGlobal.AudioContext === "function") {
    return audioGlobal.AudioContext;
  }
  return typeof audioGlobal.webkitAudioContext === "function"
    ? audioGlobal.webkitAudioContext
    : null;
}

class WebAudioCountdownBackend implements CountdownAudioBackend {
  constructor(private readonly context: AudioContext) {}

  resume(): void | Promise<void> {
    if (this.context.state === "suspended") return this.context.resume();
  }

  play(cue: CountdownSoundCue): void {
    const schedule = (): void => {
      if (this.context.state === "closed") return;
      const firstStartAt = this.context.currentTime + 0.01;
      const toneDurationSeconds = Math.max(0.05, cue.durationMs / 1_000);

      cue.frequenciesHz.forEach((frequencyHz, index) => {
        const startAt = firstStartAt + index * 0.16;
        const stopAt = startAt + toneDurationSeconds;
        const oscillator = this.context.createOscillator();
        const gain = this.context.createGain();
        oscillator.type = cue.kind === "final" ? "sine" : "triangle";
        oscillator.frequency.setValueAtTime(frequencyHz, startAt);
        gain.gain.setValueAtTime(0.0001, startAt);
        gain.gain.exponentialRampToValueAtTime(
          cue.kind === "final" ? 0.2 : 0.13,
          startAt + 0.018,
        );
        gain.gain.exponentialRampToValueAtTime(0.0001, stopAt);
        oscillator.connect(gain);
        gain.connect(this.context.destination);
        oscillator.start(startAt);
        oscillator.stop(stopAt + 0.01);
      });
    };

    if (this.context.state === "suspended") {
      void this.context.resume().then(schedule).catch(() => undefined);
      return;
    }
    schedule();
  }

  close(): void | Promise<void> {
    if (this.context.state !== "closed") return this.context.close();
  }
}

export function createWebAudioCountdownBackend(): CountdownAudioBackend | null {
  const AudioContextClass = readAudioContextConstructor();
  if (AudioContextClass === null) return null;

  try {
    return new WebAudioCountdownBackend(new AudioContextClass());
  } catch {
    return null;
  }
}
